/**
 * The app font, chosen per user (avatar menu > Appearance > Font, and User
 * Settings > Appearance). A user with no saved choice gets DEFAULT_APP_FONT.
 *
 * Inter is bundled with the app (theme/fonts.css). The other web fonts come
 * from Google Fonts: one stylesheet per family, weights 400-700, `display=swap`, so the fallback stack shows until the face
 * arrives. A family's stylesheet is added only once a user picks it. Segoe UI
 * is the system font and downloads nothing; on a Mac or iPad its stack falls
 * through to the system font.
 *
 * The choice is applied by setting --font-sans on <html>. Every surface reads
 * that token (globalStyles.css, the MUI theme, AppGrid), including dialogs
 * and popups in portals, since those
 * still sit under <html>.
 */

export type AppFontKey = 'inter' | 'plex' | 'source' | 'public' | 'opensans' | 'segoe';

export interface AppFont {
  key: AppFontKey;
  name: string;
  /** Google Fonts family name; absent for a font that needs no download. */
  google?: string;
  stack: string;
}

/**
 * The font for anyone who has not picked one, and for a saved value that is
 * no longer offered. Changing the app's default is this one line.
 */
export const DEFAULT_APP_FONT: AppFontKey = 'segoe';

/** Shown while a web font downloads, and if it never does. */
const FALLBACK = "'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif";

export const APP_FONTS: readonly AppFont[] = [
  { key: 'inter', name: 'Inter', stack: `'Inter', 'Inter Fallback', ${FALLBACK}` },
  { key: 'plex', name: 'IBM Plex Sans', google: 'IBM Plex Sans', stack: `'IBM Plex Sans', ${FALLBACK}` },
  { key: 'source', name: 'Source Sans 3', google: 'Source Sans 3', stack: `'Source Sans 3', ${FALLBACK}` },
  { key: 'public', name: 'Public Sans', google: 'Public Sans', stack: `'Public Sans', ${FALLBACK}` },
  { key: 'opensans', name: 'Open Sans', google: 'Open Sans', stack: `'Open Sans', ${FALLBACK}` },
  { key: 'segoe', name: 'Segoe UI', stack: "'Segoe UI Variable Text', 'Segoe UI', system-ui, -apple-system, sans-serif" },
];

export function isAppFont(v: unknown): v is AppFontKey {
  return APP_FONTS.some((f) => f.key === v);
}

/** A saved value as a font key; anything unknown (or since removed) is the default. */
export function toAppFont(v: unknown): AppFontKey {
  return isAppFont(v) ? v : DEFAULT_APP_FONT;
}

export function getAppFont(key: AppFontKey): AppFont {
  return APP_FONTS.find((f) => f.key === key) ?? APP_FONTS.find((f) => f.key === DEFAULT_APP_FONT)!;
}

const enc = (family: string) => family.replace(/ /g, '+');

/** The Google Fonts stylesheet for one family, weights 400-700. */
export function googleFontUrl(family: string): string {
  return `https://fonts.googleapis.com/css2?family=${enc(family)}:wght@400;500;600;700&display=swap`;
}

function addStylesheet(id: string, href: string): void {
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.id = id;
  link.href = href;
  document.head.appendChild(link);
}

const fullId = (key: AppFontKey) => `app-font-${key}`;
const previewId = (key: AppFontKey) => `app-font-preview-${key}`;

/**
 * Put a font on the page: --font-sans on <html>, which outranks the value in
 * cssVariables.css and ui2026.css.
 *
 * Inter's l / I character variants (cv05, cv08, set on :root in fonts.css)
 * are switched off for the other fonts: those tags mean something else, or
 * nothing, in another face.
 */
export function applyAppFont(key: AppFontKey): void {
  const root = document.documentElement;
  const font = getAppFont(key);
  if (font.google) {
    // The label preview is a subset holding only the name's letters; drop it
    // so it cannot stand in for the full face.
    document.getElementById(previewId(font.key))?.remove();
    addStylesheet(fullId(font.key), googleFontUrl(font.google));
  }
  root.style.setProperty('--font-sans', font.stack);
  if (font.key === 'inter') root.style.removeProperty('font-feature-settings');
  else root.style.setProperty('font-feature-settings', 'normal');
}

/**
 * Let each choice's label draw in its own face. Google Fonts' `text=` returns
 * a subset with just those letters (a few hundred bytes), so showing the
 * picker does not download the fonts themselves. Skips any family already
 * loaded in full.
 */
export function loadAppFontPreviews(): void {
  for (const font of APP_FONTS) {
    if (!font.google || document.getElementById(fullId(font.key))) continue;
    addStylesheet(
      previewId(font.key),
      `https://fonts.googleapis.com/css2?family=${enc(font.google)}:wght@500&text=${encodeURIComponent(font.name)}&display=swap`,
    );
  }
}
